import { useEffect, useState } from 'react';
import type { Product } from '@/types';
import { api } from '@/client/lib/api';
import { ProductGrid } from '@/components/ProductGrid';
import { useI18n } from '@/client/i18n/I18nContext';
import { pickInOrder } from '@/lib/catalog/listing';

/**
 * "In this article" block: the products an Edit article links to
 * (`relatedProductSlugs`), read live from the store listing (one request) so
 * price, stock and pre-order state are always current. Renders nothing until
 * loaded, or when none of the linked products is currently listed.
 */
export function ArticleProducts({ slugs }: { slugs: string[] }) {
  const { t } = useI18n();
  const [products, setProducts] = useState<Product[]>([]);
  const key = slugs.join(',');

  useEffect(() => {
    let alive = true;
    setProducts([]);
    if (!key) return;
    api
      .products()
      .then((listed) => {
        if (alive) setProducts(pickInOrder(listed, key.split(',')));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [key]);

  if (products.length === 0) return null;
  return (
    <section
      aria-labelledby="article-products-title"
      style={{ marginTop: 'var(--space-8)', paddingTop: 'var(--space-6)', borderTop: 'var(--border-hairline)' }}
    >
      <h2 id="article-products-title" className="eyebrow" style={{ marginBottom: 'var(--space-5)' }}>
        {t('article.productsTitle')}
      </h2>
      <ProductGrid products={products} compact />
    </section>
  );
}
